import React, { useState, useEffect, useCallback } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import AdminHonorRollDetailView from './AdminHonorRollDetailView';
import './AdminHonorRollView.css';
import { scopeForAdmin, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

function AdminHonorRollView({ adminData }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [loading, setLoading] = useState(true);

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminHonorRoll: Loading classes, schoolId:', adminData?.schoolId);
    try {
      const classesData = scopeForAdmin(
        await dataCacheService.getDocs(ck('classes'), collection(db, 'classes')),
        adminData
      );
      console.log('🔥 AdminHonorRoll: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminHonorRoll: Error loading classes:', error);
    }
  }, [adminData]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminHonorRoll: Loading students');
    try {
      // Cached full subcollection; isActive === true matches the old
      // where('isActive', '==', true) query (docs missing the flag excluded).
      const perClass = await Promise.all(classes.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      const studentsData = perClass.flat().filter(s => s.isActive === true);
      console.log('🔥 AdminHonorRoll: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminHonorRoll: Error loading students:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  const loadGrades = useCallback(async () => {
    console.log('🔥 AdminHonorRoll: Loading grades');
    try {
      const gradesData = [];
      await Promise.all(classes.map(async (cls) => {
        const assignmentDocs = await dataCacheService.getDocs(ck(`classes/${cls.id}/assignments`), collection(db, 'classes', cls.id, 'assignments'));
        const gradesPerAssignment = await Promise.all(assignmentDocs.map(a =>
          dataCacheService.getDocs(ck(`classes/${cls.id}/assignments/${a.id}/grades`), collection(db, 'classes', cls.id, 'assignments', a.id, 'grades'))
        ));
        gradesPerAssignment.flat().forEach(g => gradesData.push(g));
      }));
      console.log('🔥 AdminHonorRoll: Grades loaded', gradesData.length, gradesData);
      setGrades(gradesData);
    } catch (error) {
      console.error('🔥 AdminHonorRoll: Error loading grades:', error);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadStudents();
      loadGrades();
    }
  }, [classes, loadStudents, loadGrades]);

  const calculateStudentAverage = (studentId) => {
    const studentGrades = grades.filter(grade => grade.studentId === studentId);
    if (studentGrades.length === 0) return 0;
    const sum = studentGrades.reduce((acc, grade) => acc + (grade.score / grade.maxScore) * 100, 0);
    return (sum / studentGrades.length).toFixed(1);
  };

  const getHonorRollStatus = (average) => {
    const avg = parseFloat(average);
    if (avg >= 95) return { status: 'Principal\'s List', color: '#dc2626', icon: '🏆' };
    if (avg >= 90) return { status: 'High Honor Roll', color: '#14532d', icon: '⭐' };
    if (avg >= 85) return { status: 'Honor Roll', color: '#22c55e', icon: '🌟' };
    return { status: 'Not Eligible', color: '#666', icon: '📝' };
  };

  const getFilteredStudents = () => {
    const studentsWithGrades = students.map(student => {
      const average = calculateStudentAverage(student.id);
      const honorRoll = getHonorRollStatus(average);
      return { ...student, average, honorRoll };
    });

    let filtered = studentsWithGrades;
    
    if (selectedClass !== 'all') {
      filtered = filtered.filter(student => student.classId === selectedClass);
    }

    return filtered.filter(student => student.honorRoll.status !== 'Not Eligible')
                     .sort((a, b) => b.average - a.average);
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.name : 'Unknown';
  };

  const createShareableContent = (student) => {
    const content = `
🏆 ACADEMIC HONOR ROLL 🏆
===============================

Student: ${student.fullName}
Student ID: ${student.studentId}
Class: ${getClassName(student.classId)}

Honor Roll Status: ${student.honorRoll.status}
Overall Average: ${student.average}%

Generated on ${new Date().toLocaleDateString()}
Generated by KN Education Portal
    `.trim();
    return content;
  };

  const handleShare = (student) => {
    const content = createShareableContent(student);
    if (navigator.share) {
      navigator.share({
        title: 'Honor Roll Certificate',
        text: content
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(content).then(() => {
        alert('Honor roll details copied to clipboard!');
      }).catch(console.error);
    }
  };

  const handlePrint = (student) => {
    const content = createShareableContent(student);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Honor Roll Certificate</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
            pre { white-space: pre-wrap; font-family: Arial, sans-serif; }
          </style>
        </head>
        <body>
          <pre>${content}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // === Full-screen honor roll detail page (existing detail view) ===
  if (selectedStudentId) {
    console.log('🔥 AdminHonorRoll: Showing detail for student', selectedStudentId);
    return (
      <AdminHonorRollDetailView
        adminData={adminData}
        studentId={selectedStudentId}
        onBack={() => setSelectedStudentId(null)}
      />
    );
  }

  const honorRollStudents = getFilteredStudents();

  return (
    <div className="admin-honor-roll-view">
      <div className="view-header">
        <h2>Honor Roll</h2>
      </div>

      <div className="filter-bar">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(cls => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      <div className="stats-section">
        <h3>Honor Roll Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-info">
              <h3>{honorRollStudents.filter(s => s.honorRoll.status === 'Principal\'s List').length}</h3>
              <p>Principal's List</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-info">
              <h3>{honorRollStudents.filter(s => s.honorRoll.status === 'High Honor Roll').length}</h3>
              <p>High Honor Roll</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🌟</div>
            <div className="stat-info">
              <h3>{honorRollStudents.filter(s => s.honorRoll.status === 'Honor Roll').length}</h3>
              <p>Honor Roll</p>
            </div>
          </div>
        </div>
      </div>

      <p className="count-text">Total Honor Roll Students: {honorRollStudents.length}</p>

      {honorRollStudents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">⭐</div>
          <h3>No Honor Roll Students</h3>
          <p>No students currently on honor roll</p>
        </div>
      ) : (
        <div className="honor-roll-list">
          {honorRollStudents.map(student => (
            <div
              key={student.id}
              className="honor-roll-card"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedStudentId(student.id)}
            >
              <div className="student-avatar">
                {student.firstName ? student.firstName[0] : 'S'}
              </div>
              <div className="student-info">
                <h4>{student.fullName}</h4>
                <p>Student ID: {student.studentId}</p>
                <p>Class: {getClassName(student.classId)}</p>
              </div>
              <div className="honor-status">
                <span className="honor-icon">{student.honorRoll.icon}</span>
                <span 
                  className="honor-badge" 
                  style={{ background: `${student.honorRoll.color}20`, color: student.honorRoll.color }}
                >
                  {student.honorRoll.status}
                </span>
              </div>
              <div className="gpa-display">
                <span className="gpa-value">{student.average}%</span>
              </div>
              <div className="action-buttons">
                <button
                  className="share-button"
                  onClick={(e) => { e.stopPropagation(); handleShare(student); }}
                  title="Share Honor Roll"
                >
                  Share
                </button>
                <button
                  className="print-button"
                  onClick={(e) => { e.stopPropagation(); handlePrint(student); }}
                  title="Print Honor Roll"
                >
                  Print
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminHonorRollView;
